// shared-lib/dimensioning/apply.js
//
// APPLYING a stated dimension: where the subject would have to sit for the
// statement to be true.
//
// This is the piece the original sequence missed. Stating a record and
// ENFORCING one were both in the plan; actually MOVING the subject to satisfy
// it was in neither — so a button reading "Centre left to right" wrote a
// record and printed two unequal numbers. Whatever the drawing reports, a
// control that says centre has to centre.
//
// PURE — returns the target centre; the caller writes it.

const {
  resolveDatum,
  resolveAxis,
  dotXZ,
  datumEdgeToward,
  axisModeFor,
} = require('./datums');
const { normalizeRef } = require('../group-customization/refs');

// Below this the subject is already where the statement wants it, and moving
// it would be churn. Same 0.5" noise floor the rules judge "centred" by.
const { EQ_TOLERANCE_M, resolveDimensions } = require('./rules');

const lookupEntity = (id, ctx) => {
  const byId = ctx?.entitiesById;
  if (!byId) return null;
  return typeof byId.get === 'function' ? byId.get(id) : byId[id];
};

/**
 * The scalar on `u` the subject must reach, or null when the record names no
 * such position (`offset-from` carries no distance to move to).
 */
function targetScalar(record, ctx, u, s0) {
  const refs = Array.isArray(record.datum) ? record.datum : [record.datum];

  if (record.rule === 'centered-between') {
    const a = resolveDatum(refs[0], ctx, u);
    const b = resolveDatum(refs[1], ctx, u);
    if (!a || !b) return null;
    // The INNER faces — the two surfaces the subject sits between. Read from
    // the datums' order rather than from the subject's current side, so the
    // target does not depend on where the subject happens to be now.
    const [lo, hi] = a.center <= b.center ? [a, b] : [b, a];
    // The INNER faces. For a wall that is the surface facing into the room,
    // not the centreline inside it — centring between two walls means equal
    // clear distances, which is what a tape would measure.
    return (lo.hi + hi.lo) / 2;
  }

  if (record.rule === 'centered-on') {
    const d = resolveDatum(refs[0], ctx, u);
    return d ? d.center : null;
  }

  if (record.rule === 'offset-from') {
    // Only a record carrying a STATED value names a position. Without one it
    // reports the distance that happens to be there, and there is nothing to
    // move to.
    if (!Number.isFinite(record.value)) return null;
    const d = resolveDatum(refs[0], ctx, u);
    if (!d) return null;
    // Keep the subject on the side of the datum it is already on, and measure
    // from the same reference the drawn dimension does — a wall's face,
    // anything else's centreline.
    const side = s0 >= d.center ? 1 : -1;
    return datumEdgeToward(d, s0) + side * record.value;
  }

  return null;
}

/**
 * Where the subject must sit for its record to be true. PURE.
 *
 * @returns {{x:number, z:number}|null} the new centre, or null when the record
 *   names no position, cannot be resolved, or is already satisfied.
 */
function centringTarget(record, ctx) {
  const u = resolveAxis(record, ctx, axisModeFor(record?.rule));
  const subjectRef = record ? normalizeRef(record.subject) : null;
  const entity = subjectRef?.id ? lookupEntity(subjectRef.id, ctx) : null;
  const c = entity?.center;
  if (!u || !c || typeof c.x !== 'number' || typeof c.z !== 'number') {
    return null;
  }

  const s0 = dotXZ(c, u);
  const target = targetScalar(record, ctx, u, s0);
  if (target === null || !Number.isFinite(target)) return null;

  const delta = target - s0;

  // "Already there" has to mean the same thing the DRAWING means, or the two
  // disagree: a centre offset shows up DOUBLED across the two legs, so an
  // object 7.6mm off centre draws legs differing by 16mm. Judged on the
  // offset it was inside the 0.5" noise floor and the mover refused to act;
  // judged on the legs it was outside it and the drawing printed numbers
  // rather than EQ. The invariant is that a record reads as centred if and
  // only if it needs no move, so the RULES are the single judge — the same
  // way `marks.js` decides whether to draw a centreline.
  const rule = record.rule;
  if (rule === 'centered-between' || rule === 'centered-on') {
    const legs = resolveDimensions([record], ctx);
    if (legs.length > 0 && legs.every((m) => m.style === 'eq')) return null;
  } else if (Math.abs(delta) <= EQ_TOLERANCE_M) {
    return null; // an offset-from is judged on its own single distance
  }
  if (delta === 0) return null;

  // Move along the axis only — the cross-axis position is not this
  // statement's business and must survive untouched.
  return { x: c.x + delta * u[0], z: c.z + delta * u[1] };
}

/**
 * Why a record that should name a position did not. PURE.
 *
 * `centringTarget` returning null is ambiguous — it means "already there", or
 * "this rule names no position", or "something would not resolve". A control
 * that says centre and then silently does not must be able to say which.
 *
 * @returns {string|null} null when there is nothing wrong.
 */
function centringBlockedReason(record, ctx) {
  const rule = record?.rule;
  if (rule !== 'centered-between' && rule !== 'centered-on') {
    if (rule === 'offset-from' && !Number.isFinite(record?.value)) return null;
    if (rule !== 'offset-from') return 'that rule names no position';
  }
  const refs = Array.isArray(record.datum) ? record.datum : [record.datum];
  if (refs.some((r) => !r)) return null; // half-stated: an ordinary state

  const u = resolveAxis(record, ctx, axisModeFor(rule));
  if (!u) return 'could not work out which way that runs';

  const subjectRef = normalizeRef(record.subject);
  const entity = subjectRef?.id ? lookupEntity(subjectRef.id, ctx) : null;
  if (!entity?.center) return 'could not find the object in this room';

  for (const ref of refs) {
    if (!resolveDatum(ref, ctx, u)) {
      return `could not find ${ref.kind === 'wall' ? 'that wall' : 'that element'}`;
    }
  }
  return null;
}

module.exports = { centringTarget, targetScalar, centringBlockedReason };
